import { useMemo, useRef, useState } from 'react';

import dynamic, { LoaderComponent } from 'next/dynamic';
import { SwiperProps } from 'swiper/swiper-react';

import LinkIcon from '~/icons/link.svg';
import { IImageMedia, IM70MediaItem, IVideoMedia } from '~/lib/data-contract';
import { CarouselProps } from '~/shared/components/Carousel/Carousel';
import { Icon } from '~/shared/components/Icon/Icon';
import { isMobileDevice, isTouchDevice } from '~/shared/utils/platform';

const Carousel = dynamic(() => import('~/shared/components/Carousel/Carousel').then((module) => module.Carousel) as LoaderComponent<CarouselProps>, { ssr: false });

import styles from './gallery.module.css';
import { GalleryItem } from './GalleryItem';

export type GalleryItem = (IImageMedia | IVideoMedia) & {
	description?: string | JSX.Element;
};
export type GalleryProps = {
	mediaItems: IImageMedia[] | IVideoMedia[] | IM70MediaItem[];
	initialSlide: number;
};

export const isImageMedia = (item: any): item is IImageMedia => item.type === 'imageMedia';
export const isVideoMedia = (item: any): item is IVideoMedia => item.type === 'videoMedia';

export const Gallery = ({ mediaItems = [], initialSlide = 0 }: GalleryProps) => {
	const galleryItems = useMemo<GalleryItem[]>(() => {
		const items = [];

		for (const mediaItem of mediaItems) {
			if (isVideoMedia(mediaItem)) {
				items.push(mediaItem);
			} else if (isImageMedia(mediaItem)) {
				items.push(mediaItem);
			} else if (mediaItem?.video) {
				items.push({
					...mediaItem.video,
					description: mediaItem.text?.html,
				});
			} else if (mediaItem?.image) {
				items.push({
					...mediaItem.image,
					description: mediaItem.text?.html,
				});
			}
		}

		return items;
	}, [mediaItems]);

	const [index, setIndex] = useState(initialSlide);
	const prevRef = useRef<HTMLButtonElement>(null);
	const nextRef = useRef<HTMLButtonElement>(null);
	const carouselOptions: SwiperProps = {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		zoom: {
			toggle: isTouchDevice && isMobileDevice, // avoid double-tap zoom on desktop and non-touchDevice
		},
		slidesPerView: 1,
		centeredSlides: true,
		initialSlide: initialSlide,
		spaceBetween: 0,
		loop: mediaItems.length > 1,
		loopPreventsSlide: false,
		freeMode: false,
		pagination: {
			clickable: true,
			type: 'fraction',
		},
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		speed: 1000,
	};

	const handleIndexChange = (currentIndex: number) => {
		setIndex(currentIndex);
	};

	return (
		<div className={styles.root}>
			<div className={styles.arrows}>
				<button
					className={styles.button}
					ref={prevRef}
					aria-label={'Show previous media item'}
				>
					<Icon
						size="md"
						aspectRatio={2}
						style={{ transform: 'rotate(180deg)' }}
						aria-hidden
						children={<LinkIcon />}
					/>
				</button>
				<button
					className={styles.button}
					ref={nextRef}
					aria-label={'Show next media item'}
				>
					<Icon
						size="md"
						aspectRatio={2}
						aria-hidden
						children={<LinkIcon />}
					/>
				</button>
			</div>
			<Carousel
				optionsOverride={carouselOptions}
				onSlideChange={(swiper) => handleIndexChange(swiper.realIndex)}
				prevRef={prevRef}
				nextRef={nextRef}
				items={galleryItems.map((media, i) => (
					<GalleryItem
						key={`gallery_item_${i}`}
						active={i === index}
						item={media}
					/>
				))}
			/>
		</div>
	);
};
